import React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getMyPost,
  getWhatIsMyBusiness,
  deleteMyPost,
  reorderMyPosts,
  getPlansListing,
  PayForCarSale,
} from '../../Redux/Actions/UserAction';
import Sidebar from '../common/Sidebar/Sidebar';
import { useTranslation } from 'react-i18next';
import { getSettingsSidebarOptions } from './sidebarOptions';
import Swal from 'sweetalert2';
import { pickCarImage } from '../../utils/pickCarImage';

export default function MyPosts() {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  // English in English mode, Arabic in Arabic mode (fallback to whichever exists).
  const L = (en, ar) => (isRTL ? ar || en : en || ar);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getMyPost());
    dispatch(getWhatIsMyBusiness());
    dispatch(getPlansListing()); // packages for the Activate/Renew flow
  }, [dispatch]);
  const myPosts = useSelector((state) => state.home?.userMyPostLists);
  const getMybusiness = useSelector((state) => state.home?.getMybusiness);
  const allPlans = useSelector((state) => state.home?.getAllPlansList);
  const planList = allPlans
    ? Object.keys(allPlans).map((key) => allPlans[key])
    : [];

  const sideBarOpt = getSettingsSidebarOptions(t, getMybusiness);

  // [CF] The carSaleId currently being published/renewed (drives the package
  // modal). A post is publishable when it has never been paid (expiryDetails
  // null) or its paid period has lapsed.
  const [payForId, setPayForId] = useState(null);

  // Derive a post's publish state from expiryDetails (returned by My Posts).
  const postStatus = (post) => {
    const ed = post?.expiryDetails;
    if (!ed || !ed.expiryDate) {
      return { key: 'unpaid', label: t('Not published'), color: '#dc3545' };
    }
    const exp = new Date(ed.expiryDate).getTime();
    const now = Date.now();
    if (exp > now) {
      const days = Math.max(0, Math.ceil((exp - now) / 86400000));
      return {
        key: 'active',
        label: `${t('Active')} · ${days} ${t('days left')}`,
        color: '#28a745',
      };
    }
    return { key: 'expired', label: t('Expired'), color: '#fd7e14' };
  };

  // Open the package picker for a post, or pay with the chosen package. The
  // pay-car-sale POST takes a few seconds to build the Tap charge, so show a
  // blocking loader until it redirects to KNET (or activates a free plan).
  const onChoosePackage = (plan) => {
    const packId = plan?.advertisementPackId;
    if (!packId || !payForId) return;
    const carSaleId = payForId;
    setPayForId(null);
    Swal.fire({
      title: t('Redirecting to payment…'),
      text: t('Please wait, do not close this window.'),
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });
    dispatch(PayForCarSale({ paymentPlan: packId, carSaleId }));
  };

  // [CF] Drag-to-reorder the posts. `orderList` is an optimistic local copy of
  // myPosts; mutated live during a drag and persisted silently on drop.
  const [orderList, setOrderList] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    setOrderList(Array.isArray(myPosts) ? myPosts : []);
  }, [myPosts]);

  const onDragStart = (index) => () => setDragIndex(index);

  const onDragEnter = (index) => () => {
    if (dragIndex === null || dragIndex === index) return;
    setOrderList((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  const onDragEndPersist = () => {
    setDragIndex(null);
    const ids = orderList.map((p) => p?.carSaleId).filter(Boolean);
    if (ids.length) dispatch(reorderMyPosts(ids));
  };

  const onDelete = (carSaleId) => {
    Swal.fire({
      title: t('Are you sure?'),
      text: t('This post will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3ab1f7',
      confirmButtonText: t('Delete'),
      cancelButtonText: t('Cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteMyPost(carSaleId));
      }
    });
  };

  return (
    <div>
      <div class="main-content-area">
        <div class="container">
          <h2 class="mb-4">{t('Settings')}</h2>

          <div class="row justify-content-between">
            <Sidebar options={sideBarOpt} showPackage={false} />
            <div class="col-12 col-sm-12 col-md-9 col-lg-9">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="mb-0">{t('My Posts')}</h5>
                <Link to="/sp-cars-for-sale" class="btn btn-warning btn-sm">
                  <i class="fas fa-plus"></i> {t('Add Car for Sale')}
                </Link>
              </div>

              {orderList?.length > 1 && (
                <p className="text-muted small mb-2">
                  <i class="fas fa-arrows-alt"></i>{' '}
                  {t('Drag posts to reorder how they appear')}
                </p>
              )}
              <div className="row">
                {orderList?.length > 0 ? (
                  orderList?.map((post, ind) => {
                    // [CF 2026-07-28] Client: posts showed the wrong car's
                    // picture. This matched LOGO before any of the car-photo
                    // types, so a dealer logo could win over the actual car, and
                    // MAIN_BANNER — the photo the vendor nominates as primary,
                    // and the one the app shows — was never preferred.
                    const img = pickCarImage(post?.businessMedia);
                    const st = postStatus(post);
                    return (
                      <div
                        key={post?.carSaleId || ind}
                        className="col-6 col-sm-6 col-md-4 col-lg-4 mb-4"
                        draggable
                        onDragStart={onDragStart(ind)}
                        onDragEnter={onDragEnter(ind)}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnd={onDragEndPersist}
                        onDrop={(e) => e.preventDefault()}
                        style={{
                          cursor: 'move',
                          opacity: dragIndex === ind ? 0.5 : 1,
                        }}
                      >
                        <div className="user-car-dealers-wrap">
                          <div
                            className="line-a"
                            style={{ position: 'relative' }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                top: '8px',
                                [isRTL ? 'right' : 'left']: '8px',
                                zIndex: 2,
                                background: st.color,
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: '12px',
                              }}
                            >
                              {st.label}
                            </span>
                            <Link
                              to={`/user-cars-for-sale-detail/${post?.carSaleId}`}
                            >
                              <img
                                style={{
                                  width: '100%',
                                  height: '200px',
                                  borderRadius: '10px',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                                src={
                                  img ||
                                  'assets/images/service-provider/car-sale/car.jpg'
                                }
                                alt=""
                                className="img-fluid"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    'assets/images/service-provider/car-sale/car.jpg';
                                }}
                              />
                            </Link>
                          </div>
                          <div className="line-b">
                            <div className="row">
                              <div className="col-7 col-lg-8">
                                <div
                                  className="line-ba"
                                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                                >
                                  {L(post?.brand?.name, post?.brand?.nameAr) ||
                                    post?.carName}
                                </div>
                                <div
                                  className="line-bb"
                                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                                >
                                  {post?.carModel}
                                </div>
                                <div
                                  className="line-bb"
                                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                                >
                                  {post?.modelYear?.modelYear}
                                </div>
                              </div>
                              <div className="col-5 col-lg-4">
                                <div className="row no-gutters">
                                  <div className="col-6">
                                    <Link
                                      to={`/sp-cars-for-sale?edit=${post?.carSaleId}`}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title={t('Edit')}
                                      className="ss-btn"
                                    >
                                      <i className="far fa-edit"></i>
                                    </Link>
                                  </div>
                                  <div className="col-6">
                                    <button
                                      onClick={() => onDelete(post?.carSaleId)}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title={t('Delete')}
                                      className="ss-btn"
                                      style={{
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        color: 'inherit',
                                        padding: 0,
                                        fontSize: 'inherit',
                                      }}
                                    >
                                      <i className="far fa-trash-alt"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="line-c">
                            <div className="row justify-content-between">
                              <div className="col-auto">
                                <span
                                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                                >
                                  {post?.kilometerDriven} KM
                                </span>
                              </div>
                              <div className="col-auto">
                                <span
                                  style={{ textAlign: isRTL ? 'right' : 'left' }}
                                >
                                  KD {post?.price}
                                </span>
                              </div>
                            </div>
                          </div>
                          {st.key !== 'active' && (
                            <button
                              type="button"
                              onClick={() => setPayForId(post?.carSaleId)}
                              className="btn btn-warning btn-sm btn-block mt-2"
                            >
                              <i class="fas fa-bullhorn"></i>{' '}
                              {st.key === 'expired'
                                ? t('Renew')
                                : t('Publish / Promote')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <h6 className="text-center w-100">{t('No Posts Found')}</h6>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* [CF] Package picker for publishing / renewing a post. Lightweight
          overlay (no Bootstrap JS) — reuses PayForCarSale for any carSaleId so
          an abandoned/expired post can be paid for later from here. */}
      {payForId && (
        <div
          onClick={() => setPayForId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '720px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              padding: '20px',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                {t('Choose a package to publish your car')}
              </h5>
              <button
                type="button"
                className="close"
                onClick={() => setPayForId(null)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <p style={{ color: '#777' }}>
              {t('Select a package to publish this car on the website.')}
            </p>
            <div className="row">
              {planList && planList.length ? (
                planList.map((plan) => {
                  const isFree =
                    Number(plan?.amount) === 0 ||
                    String(plan?.advertisementPackId || '')
                      .toUpperCase()
                      .includes('FREE');
                  return (
                    <div
                      className="col-12 col-sm-6 col-md-4 mb-3"
                      key={plan?.advertisementPackId}
                    >
                      <div className="card h-100" style={{ borderRadius: '10px' }}>
                        <div className="card-body text-center">
                          <h5 className="mb-2">{plan?.name}</h5>
                          <div
                            className="mb-2"
                            style={{ fontSize: '20px', fontWeight: 700 }}
                          >
                            {isFree
                              ? t('Free')
                              : `${plan?.currency || 'KWD'} ${plan?.amount}`}
                          </div>
                          <div className="mb-3" style={{ color: '#777' }}>
                            {plan?.duration} {t('days')}
                          </div>
                          <button
                            type="button"
                            className="btn btn-warning btn-block"
                            onClick={() => onChoosePackage(plan)}
                          >
                            {isFree ? t('Publish Free') : t('Select & Pay')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-12 text-center" style={{ color: '#777' }}>
                  {t('No packages available right now.')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
